import React from 'react';
import { Layout, Empty, Spin, Tabs, Button } from 'antd';
import { EyeOutlined, LogoutOutlined, LineChartOutlined, RadarChartOutlined, BellOutlined, ScheduleOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/useTheme';
import { useActiveEvent } from '../../hooks/useActiveEvent';
import { MobileStationsObservations } from './MobileStationsObservations';
import { MobileForecastView } from './MobileForecastView';
import { MobileNowcastingView } from './MobileNowcastingView';
import { MobileAlertsView } from './MobileAlertsView';
import { MobileJobsView } from './MobileJobsView';
import { ROUTES } from '../../utils/constants';

const { Header, Content } = Layout;

type MobileView = 'observations' | 'forecast' | 'nowcasting' | 'alerts' | 'jobs';

const pathToView: Record<string, MobileView> = {
  [ROUTES.FORECAST]: 'forecast',
  [ROUTES.NOWCASTING]: 'nowcasting',
  [ROUTES.OBSERVATIONS]: 'observations',
  [ROUTES.ALERTS]: 'alerts',
  [ROUTES.JOBS]: 'jobs',
};

const viewToPath: Record<MobileView, string> = {
  forecast: ROUTES.FORECAST,
  nowcasting: ROUTES.NOWCASTING,
  observations: ROUTES.OBSERVATIONS,
  alerts: ROUTES.ALERTS,
  jobs: ROUTES.JOBS,
};

export const MobileLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, authState } = useAuth();
  const { theme } = useTheme();
  const { activeEvent, loading, error, refresh } = useActiveEvent();

  const hasAdminRole = authState.user?.role === 'root';

  if (location.pathname === ROUTES.CLIMATE_RECAP) {
    return <Navigate to={ROUTES.FORECAST} replace />;
  }

  if (location.pathname === ROUTES.JOBS && !hasAdminRole) {
    return <Navigate to={ROUTES.FORECAST} replace />;
  }

  const activeView = (pathToView[location.pathname] ?? 'forecast') as MobileView;

  const isDark = theme === 'dark';
  const bg = isDark ? '#141414' : '#f5f5f5';
  const headerBg = isDark ? '#1f1f1f' : '#fff';
  const textColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)';
  const subText = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)';

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  return (
    <Layout
      style={{
        flex: 1,
        minHeight: 0,
        background: bg,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Header
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: headerBg,
          padding: '0 16px',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <img
            src={theme === 'dark' ? '/tio-logo-white.svg' : '/tio-logo-dark.svg'}
            alt="Tomorrow.io"
            style={{ height: 24, width: 'auto', opacity: 0.9, objectFit: 'contain' }}
          />
          <div
            style={{
              background: '#102D5C',
              padding: '4px 8px',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <img
              src="/fia-logo.svg"
              alt="FIA"
              style={{ height: 22, width: 'auto', display: 'block' }}
            />
          </div>
          <span
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: textColor,
              marginLeft: 4,
            }}
          >
            FIA Weather
          </span>
        </div>
        <Button
          type="text"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
          style={{ color: subText }}
        />
      </Header>

      <Content
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 240,
              gap: 16,
            }}
          >
            <Spin size="large" />
            <span style={{ color: subText }}>Loading active event…</span>
          </div>
        ) : error ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <p style={{ color: textColor, marginBottom: 12 }}>{error}</p>
            <Button onClick={refresh}>Retry</Button>
          </div>
        ) : !activeEvent ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 280,
              padding: 24,
              textAlign: 'center',
            }}
          >
            <Empty
              description={
                <span style={{ color: textColor, fontSize: 16 }}>
                  No active event
                </span>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <p style={{ color: subText, marginTop: 8, maxWidth: 280 }}>
                There is no event taking place today. Check back when a race weekend is underway.
              </p>
            </Empty>
          </div>
        ) : (
          <>
            <style>{`
              .mobile-layout-tabs {
                flex: 1;
                min-height: 0;
                display: flex;
                flex-direction: column;
                overflow: hidden;
              }
              .mobile-layout-tabs > .ant-tabs-nav {
                flex-shrink: 0;
                margin: 0 !important;
              }
              .mobile-layout-tabs > .ant-tabs-content-holder {
                flex: 1;
                min-height: 0;
                overflow: hidden;
              }
              .mobile-layout-tabs > .ant-tabs-content-holder > .ant-tabs-content {
                height: 100%;
              }
              .mobile-layout-tabs .ant-tabs-content > .ant-tabs-tabpane {
                height: 100%;
              }
              .mobile-layout-tabs .ant-tabs-tabpane {
                overflow: hidden;
              }
            `}</style>
            {/* Event title */}
            <div
              style={{
                flexShrink: 0,
                padding: '16px 16px 8px',
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 600,
                  color: textColor,
                }}
              >
                {activeEvent.circuit?.display_name ?? activeEvent.name}
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: subText }}>
                {activeEvent.start_date} – {activeEvent.end_date}
              </p>
            </div>

            {/* View tabs */}
            <Tabs
              className="mobile-layout-tabs"
              activeKey={activeView}
              onChange={(k) => navigate(viewToPath[k as MobileView] ?? ROUTES.FORECAST)}
              destroyOnHidden
              items={[
                {
                  key: 'forecast',
                  label: (
                    <span>
                      <LineChartOutlined style={{ marginRight: 6 }} />
                      Forecast
                    </span>
                  ),
                  children: (
                    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                      <MobileForecastView event={activeEvent} theme={theme} />
                    </div>
                  ),
                },
                {
                  key: 'nowcasting',
                  label: (
                    <span>
                      <RadarChartOutlined style={{ marginRight: 6 }} />
                      Nowcasting
                    </span>
                  ),
                  children: (
                    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                      <MobileNowcastingView event={activeEvent} theme={theme} />
                    </div>
                  ),
                },
                {
                  key: 'observations',
                  label: (
                    <span>
                      <EyeOutlined style={{ marginRight: 6 }} />
                      Observations
                    </span>
                  ),
                  children: (
                    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                      <MobileStationsObservations event={activeEvent} theme={theme} />
                    </div>
                  ),
                },
                {
                  key: 'alerts',
                  label: (
                    <span>
                      <BellOutlined style={{ marginRight: 6 }} />
                      Alerts
                    </span>
                  ),
                  children: (
                    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                      <MobileAlertsView event={activeEvent} theme={theme} />
                    </div>
                  ),
                },
                ...(hasAdminRole
                  ? [
                      {
                        key: 'jobs',
                        label: (
                          <span>
                            <ScheduleOutlined style={{ marginRight: 6 }} />
                            Jobs
                          </span>
                        ),
                        children: (
                          <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            <MobileJobsView event={activeEvent} theme={theme} />
                          </div>
                        ),
                      },
                    ]
                  : []),
              ]}
              style={{ padding: '0 16px' }}
            />
          </>
        )}
      </Content>
    </Layout>
  );
};
